import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  TextField,
  InputAdornment,
  Paper,
  Container,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Search } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import BrokerCard from '../../components/BrokerCard';
import { brokers as staticBrokers, brokerDetails as staticBrokerDetails } from '../../config/staticData';
import { getBrokers as apiGetBrokers } from '../../api';
import { colors } from '../../config/colors';

// Styled Header Section
const HeaderSection = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 50%, ${colors.secondary[500]} 100%)`,
  padding: theme.spacing(6, 2),
  borderRadius: '0 0 40px 40px',
  marginBottom: theme.spacing(4),
  color: 'white',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.3,
  },
}));

const AllBrokers = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [brokers, setBrokers] = useState(staticBrokers);
  const [detailsMap, setDetailsMap] = useState(staticBrokerDetails);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await apiGetBrokers();
        if (!mounted || !list.length) return;
        setBrokers(list);
        const map = {};
        list.forEach((b) => {
          map[b.name] = {
            rating: b.rating,
            activeClients: b.activeClients,
            brokerage: b.brokerage,
            features: b.features,
            description: b.description,
          };
        });
        setDetailsMap(map);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Using default brokers');
        setBrokers(staticBrokers);
        setDetailsMap(staticBrokerDetails);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Filter brokers based on search
  const filteredBrokers = brokers.filter((broker) =>
    broker.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCompareClick = (brokerName) => {
    // Navigate to compare page with first broker as selected
    navigate(`/compare?broker1=${brokerName}&broker2=Zerodha`);
  };

  const handleViewDetails = (brokerName) => {
    const slugify = (name = '') =>
      String(name)
        .trim()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
    navigate(`/broker/${slugify(brokerName)}`);
  };

  // Get gradient color based on broker color class
  const getGradient = (colorClass) => {
    const gradientMap = {
      'from-secondary-400 to-secondary-600': `linear-gradient(135deg, ${colors.secondary[400]}, ${colors.secondary[600]})`,
      'from-primary-400 to-primary-600': `linear-gradient(135deg, ${colors.primary[400]}, ${colors.primary[600]})`,
      'from-blue-400 to-blue-600': 'linear-gradient(135deg, #60a5fa, #2563eb)',
      'from-green-400 to-green-600': 'linear-gradient(135deg, #4ade80, #16a34a)',
      'from-purple-400 to-purple-600': 'linear-gradient(135deg, #a78bfa, #9333ea)',
      'from-indigo-400 to-indigo-600': 'linear-gradient(135deg, #818cf8, #4f46e5)',
    };
    return gradientMap[colorClass] || gradientMap['from-primary-400 to-primary-600'];
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Header />
      
      <main className="flex-grow">
        {/* Header Section */}
        <HeaderSection elevation={0}>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 'bold',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
                position: 'relative',
                zIndex: 1,
              }}
            >
              All Stock Brokers
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.95,
                mb: 4,
                fontSize: { xs: '1rem', md: '1.25rem' },
                position: 'relative',
                zIndex: 1,
              }}
            >
              Compare and choose the best stock broker for your trading needs
            </Typography>

            {/* Search Bar */}
            <Box sx={{ maxWidth: 600, mx: 'auto', position: 'relative', zIndex: 1 }}>
              <TextField
                fullWidth
                placeholder="Search brokers by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                    '&:hover fieldset': {
                      borderColor: colors.primary[400],
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: colors.primary[500],
                      borderWidth: 2,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={20} color={colors.primary[500]} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Container>
        </HeaderSection>

        {/* Brokers Grid */}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {error && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          {filteredBrokers.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: '20px',
                backgroundColor: 'white',
              }}
            >
              <Typography variant="h6" color="text.secondary">
                No brokers found matching "{searchQuery}"
              </Typography>
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 3,
              }}
            >
              {filteredBrokers.map((broker) => {
                const details = detailsMap[broker.name] || {
                  rating: 4.5,
                  activeClients: '50L+',
                  brokerage: '0.05%',
                  features: ['Trading', 'Investing', 'Mobile App'],
                  description: 'Leading stock broker in India.',
                };

                return (
                  <Box key={broker.id} sx={{ minWidth: 0 }}>
                    <BrokerCard
                      broker={broker}
                      details={details}
                      logoGradient={getGradient(broker.color)}
                      onViewDetails={handleViewDetails}
                      onCompare={handleCompareClick}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </Container>
        {/* Stats Section */}
      
      </main>

      <Footer />
    </div>
  );
};

export default AllBrokers;
